import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { ChevronDown, MapPin } from 'lucide-react'
import { getStateRow, listAllStates, formatLossUSD, slugToCode } from '@/lib/ic3'
import { useStatePack } from '@/lib/state-context'

/**
 * Local US-state flag, downloaded from Wikimedia Commons and stored in
 * `public/states/flags/{code}.svg`. Loads instantly with no third-party
 * runtime dependency.
 */
function StateFlag({
  code,
  className = 'h-5 w-7',
}: {
  code: string
  className?: string
}) {
  const [failed, setFailed] = useState(false)
  if (failed) {
    return (
      <span
        className={`${className} flex shrink-0 items-center justify-center rounded-sm bg-slate-200 text-[10px] font-bold tracking-tight text-slate-600 ring-1 ring-inset ring-slate-300`}
        aria-hidden
      >
        {code.toUpperCase()}
      </span>
    )
  }
  return (
    <img
      src={`/states/flags/${code.toLowerCase()}.svg`}
      alt=""
      loading="lazy"
      decoding="async"
      className={`${className} shrink-0 rounded-sm object-cover ring-1 ring-inset ring-slate-200`}
      onError={() => setFailed(true)}
    />
  )
}

/**
 * Compact state picker shown in the header. When a state is active, the
 * trigger reads "Showing: <state>"; otherwise "Find your state".
 */
export default function StatePicker() {
  const pack = useStatePack()
  const navigate = useNavigate()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)

  // Resolve the active state from the URL (works for every state, not
  // just the curated packs). When the URL is /states/<slug>, we look up
  // the IC3 row directly so the trigger reflects whichever state the
  // user is actually viewing.
  const activeState = useMemo(() => {
    const m = location.pathname.match(/^\/states\/([a-z][a-z0-9-]+)/i)
    if (!m) return null
    const code = slugToCode(m[1])
    if (!code) return null
    const row = getStateRow(code)
    if (!row) return null
    return { code, name: row.name }
  }, [location.pathname])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const all = listAllStates()
  const filtered = query
    ? all.filter(s => s.row.name.toLowerCase().includes(query.toLowerCase()))
    : all

  // pack.name still wins when a tenant overlay is active (because the
  // pack's display name might be tenant-customized in the future).
  const triggerLabel = pack.name ?? activeState?.name ?? 'Find your state'

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-semibold text-white shadow-[0_4px_20px_-8px_rgba(106,92,255,0.6)] ring-1 ring-violet-300/20 transition-colors hover:border-white/40 hover:bg-white/15"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {activeState ? (
          <StateFlag
            code={activeState.code}
            className="h-5 w-7 ring-white/20"
          />
        ) : (
          <MapPin className="h-4 w-4 text-violet-300" />
        )}
        <span>{triggerLabel}</span>
        <ChevronDown
          className={`h-4 w-4 text-white/80 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute end-0 z-30 mt-3 w-[20rem] overflow-hidden rounded-2xl bg-white text-slate-700 shadow-[0_20px_50px_-12px_rgba(15,15,40,0.35)] ring-1 ring-slate-200/70"
        >
          <div className="border-b border-slate-100 p-3">
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={`Search ${all.length} states`}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
            />
            <p className="mt-2 px-1 text-[11px] text-slate-400">
              Showing {filtered.length} of {all.length} states. Scroll for more.
            </p>
          </div>

          <ul className="max-h-[60vh] overflow-y-auto py-1">
            {filtered.map(({ code, slug, row }) => (
              <li key={code}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false)
                    setQuery('')
                    navigate({ to: '/states/$slug', params: { slug } })
                  }}
                  className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-start text-sm hover:bg-violet-50/60"
                >
                  <StateFlag code={code} />
                  <span className="flex-1 font-medium text-slate-800">
                    {row.name}
                  </span>
                  <span className="text-xs text-slate-400">
                    {formatLossUSD(row.losses)}
                  </span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-center text-sm text-slate-400">
                No matches
              </li>
            )}
          </ul>

          <div className="border-t border-slate-100 bg-slate-50 px-3 py-2 text-end">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                setQuery('')
                navigate({ to: '/states' })
              }}
              className="cursor-pointer text-xs font-semibold text-violet-700 hover:text-violet-800"
            >
              See all states →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
